/**
 * MobileFeeEditor.tsx
 * Mobile fee editor component for viewing and editing fee configurations.
 *
 * Route-based mobile fee editor rendered by MobileFeeEditorPage.
 * Supports two modes:
 * - edit: node has its own active fee configuration (editable)
 * - inherited: node inherits fees from an ancestor (read-only display)
 *
 * Features: mode derivation (edit vs inherited), config lookups, commission form
 * via CommissionTabContent, "Customize Fees" / "Inherit from Parent" flows,
 * back-button navigation with taxonomy path, verification status footer,
 * notes popover, save handler with EditorUiState bridge to MobileFeeEditorPage,
 * and cross-tab sync via queue-while-dirty pattern with conflict banner.
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle, ArrowLeft, NotepadText, Save, ShieldCheck } from 'lucide-react';

// Workspace packages
import {
  ScrollShadow,
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Popover,
  PopoverTrigger,
  PopoverContent,
  TextArea,
  Chip,
  useToastNotification,
} from '@enfomentis/ui';

import type { BrokerFeeConfiguration } from '@/types/schema/Broker';
import type { CommissionStructure } from '@/types/shared/feeStructures/CommissionStructure';
import {
  ASSET_CLASS_TYPE_DISPLAY_MAP,
  MARKET_SEGMENT_DISPLAY_MAP,
  SUB_ASSET_CLASS_DISPLAY_MAP,
} from '@/constants/assetClass';
import { VERIFICATION_STATUS_DISPLAY_MAP } from '@/constants/feeEnums';
import { needsVerification } from '@/utils/feeEnums';

// Relative — feeTree utilities (not all re-exported from barrel)
import { buildFeeTree } from '../Brokers/feeTree/buildFeeTree';
import {
  findNodeById,
  findInheritedConfig,
  buildNodeIdFromConfig,
  buildConfigName,
  buildFeeConfigPath,
} from '../Brokers/feeTree/feeConfigUtils';
import type { EditorMode } from '../Brokers/feeEditor/feeEditorTypes';
import { CommissionTabContent } from '../Brokers/feeEditor/CommissionTabContent';
import { VERIFICATION_STATUS_COLOR_MAP } from '../Brokers/feeEditor/feeEditorUtils';
import { useBrokers } from '../Brokers/context';

// =============================================================================
// Types
// =============================================================================

/** Reactive state bridge — page reads this to render Save/Discard footer. */
export interface EditorUiState {
  isDirty: boolean;
  isSaving: boolean;
  hasActiveConfig: boolean;
}

interface MobileFeeEditorProps {
  /** Currently selected tree node ID (parent renders only when non-null) */
  selectedNodeId: string;
  /** Fee configurations from the selected broker */
  feeConfigurations: BrokerFeeConfiguration[];
  /** Callback to navigate: null = back to fee tree list, string = different node */
  onNodeSelect: (nodeId: string | null) => void;
  /** Ref kept current with editor's dirty state */
  isDirtyRef: React.MutableRefObject<boolean>;
  /** Ref populated with editor's discard callback for navigation modal */
  discardRef: React.MutableRefObject<(() => void) | null>;
  /** Ref populated with editor's save callback for save-then-navigate */
  saveRef: React.MutableRefObject<(() => Promise<boolean>) | null>;
  /** Reactive state bridge for page-level persistence UI (Save/Discard footer) */
  onEditorStateChange?: (state: EditorUiState) => void;
}

// =============================================================================
// Component
// =============================================================================

export const MobileFeeEditor: React.FC<MobileFeeEditorProps> = ({
  selectedNodeId,
  feeConfigurations,
  onNodeSelect,
  isDirtyRef,
  discardRef,
  saveRef,
  onEditorStateChange,
}) => {
  // ---------------------------------------------------------------------------
  // Context Hooks
  // ---------------------------------------------------------------------------

  const { selectedBroker, updateFeeConfiguration, addFeeConfiguration, removeFeeConfiguration } = useBrokers();
  const { showToast } = useToastNotification();

  const accountCurrency = selectedBroker?.feeMetadata?.defaultAccountCurrency?.trim() || null;

  // ---------------------------------------------------------------------------
  // Fee Tree & Node Lookup
  // ---------------------------------------------------------------------------

  const displayMaps = useMemo(
    () => ({
      assetClassType: ASSET_CLASS_TYPE_DISPLAY_MAP,
      marketSegment: MARKET_SEGMENT_DISPLAY_MAP,
      subAssetClass: SUB_ASSET_CLASS_DISPLAY_MAP,
    }),
    []
  );

  const tree = useMemo(
    () => buildFeeTree(feeConfigurations, displayMaps),
    [feeConfigurations, displayMaps]
  );

  const selectedNode = useMemo(
    () => findNodeById(tree, selectedNodeId),
    [tree, selectedNodeId]
  );

  // ---------------------------------------------------------------------------
  // Active Fee Configuration & Mode
  // ---------------------------------------------------------------------------

  const activeFeeConfig = useMemo((): BrokerFeeConfiguration | null => {
    if (!selectedNode || selectedNode.feeConfigIds.length === 0) {
      return null;
    }
    const activeConfig = feeConfigurations.find(
      (c) =>
        selectedNode.feeConfigIds.includes(c.id) && c.status === 'active'
    );
    return activeConfig ?? null;
  }, [selectedNode, feeConfigurations]);

  const mode: EditorMode = activeFeeConfig ? 'edit' : 'inherited';

  // ---------------------------------------------------------------------------
  // Nearest Ancestor Config (computed regardless of mode)
  // ---------------------------------------------------------------------------

  const nearestAncestorConfig = useMemo((): BrokerFeeConfiguration | null => {
    if (!selectedNode) return null;
    return findInheritedConfig(selectedNode, feeConfigurations);
  }, [selectedNode, feeConfigurations]);

  const nearestAncestorNodeId = useMemo((): string | null => {
    if (!nearestAncestorConfig) return null;
    return buildNodeIdFromConfig(nearestAncestorConfig);
  }, [nearestAncestorConfig]);

  const nearestAncestorLabel = useMemo((): string | null => {
    if (!nearestAncestorConfig) return null;
    return buildConfigName(
      nearestAncestorConfig.category,
      nearestAncestorConfig.instrumentId,
      displayMaps
    );
  }, [nearestAncestorConfig, displayMaps]);

  // ---------------------------------------------------------------------------
  // Local Commission State & Baseline Refs
  // ---------------------------------------------------------------------------

  const baselineCommissionRef = useRef<string>('null');
  const localCommissionJsonRef = useRef<string>('null');

  const [localCommission, setLocalCommission] = useState<CommissionStructure | null>(null);

  // Cross-tab conflict state
  const [hasQueuedFeeUpdate, setHasQueuedFeeUpdate] = useState(false);
  const [dismissedFeeConflict, setDismissedFeeConflict] = useState(false);

  // Queued external update (blocked while dirty)
  const queuedConfigIdRef = useRef<string | null>(null);
  const queuedCommissionRef = useRef<CommissionStructure | null>(null);

  // Primitive proxies for effect deps (NOT the activeFeeConfig object itself)
  const activeConfigId = activeFeeConfig?.id ?? null;
  const activeFeesUpdatedAt = activeFeeConfig?.feesUpdatedAt ?? null;

  // Track previous config ID to detect node switch vs same-config update
  const prevConfigIdRef = useRef<string | null>(null);

  // Ref for activeConfigId — used by applyQueuedFeeUpdate to check staleness
  const activeConfigIdRef = useRef<string | null>(activeConfigId);
  activeConfigIdRef.current = activeConfigId;

  // Suppresses "changed externally" toast for locally-initiated config removal (Inherit from Parent)
  const localRemovalRef = useRef(false);

  // Sync local state from active config when config ID or data changes.
  // Queue-while-dirty: if dirty AND same config, queue the external update instead of overwriting.
  useEffect(() => {
    const prevConfigId = prevConfigIdRef.current;
    const isNodeSwitch = activeConfigId !== prevConfigId;
    prevConfigIdRef.current = activeConfigId;

    // Check if commission is dirty (via JSON snapshot refs synced in render body)
    const isCurrentlyDirty = localCommissionJsonRef.current !== baselineCommissionRef.current;

    // Consume/reset localRemovalRef on any node-switch removal (regardless of dirty state)
    // so the flag never leaks across subsequent navigations.
    // Only show the warning toast when the user actually had unsaved edits.
    if (isNodeSwitch && prevConfigId !== null && !activeFeeConfig) {
      if (localRemovalRef.current) {
        localRemovalRef.current = false;
      } else if (isCurrentlyDirty) {
        showToast({ title: 'Fee configuration changed externally — unsaved edits could not be applied', color: 'warning' });
      }
    }

    // If dirty AND same config (external update, not node switch) → queue, don't overwrite
    if (isCurrentlyDirty && !isNodeSwitch && activeFeeConfig) {
      queuedConfigIdRef.current = activeConfigId;
      queuedCommissionRef.current = activeFeeConfig.commission;
      setHasQueuedFeeUpdate(true);
      setDismissedFeeConflict(false);
      return;
    }

    // Clean or new config → apply normally
    if (activeFeeConfig) {
      setLocalCommission(activeFeeConfig.commission);
      baselineCommissionRef.current = JSON.stringify(activeFeeConfig.commission);
    } else {
      setLocalCommission(null);
      baselineCommissionRef.current = 'null';
    }

    // Clear any queued state on clean apply
    queuedConfigIdRef.current = null;
    queuedCommissionRef.current = null;
    setHasQueuedFeeUpdate(false);
    setDismissedFeeConflict(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentional: uses primitive proxies (activeConfigId, activeFeesUpdatedAt) instead of activeFeeConfig object reference to avoid re-running when unrelated config fields change. JSON snapshot refs are read (not deps) for dirty guard.
  }, [activeConfigId, activeFeesUpdatedAt]);

  const handleCommissionChange = useCallback(
    (commission: CommissionStructure): void => {
      setLocalCommission(commission);
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Save / Discard Handlers
  // ---------------------------------------------------------------------------

  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!activeFeeConfig || !localCommission || isSavingRef.current) return false;

    // Dirty guard for programmatic callers (e.g. saveRef from navigation modal)
    const anyDirty = localCommissionJsonRef.current !== baselineCommissionRef.current;
    if (!anyDirty) return false;

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      const success = await updateFeeConfiguration(activeFeeConfig.id, {
        commission: localCommission,
      });
      if (success) {
        baselineCommissionRef.current = JSON.stringify(localCommission);
        // Clear full queue on save — defense-in-depth (sync effect also clears on context reload)
        queuedConfigIdRef.current = null;
        queuedCommissionRef.current = null;
        setHasQueuedFeeUpdate(false);
        setDismissedFeeConflict(false);
        showToast({ title: 'Fees saved', color: 'success' });
      } else {
        showToast({ title: 'Failed to save fees. Please try again.', color: 'danger' });
      }
      return success;
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [activeFeeConfig, localCommission, updateFeeConfiguration, showToast]);

  // ---------------------------------------------------------------------------
  // Apply Queued Fee Update (cross-tab sync)
  // ---------------------------------------------------------------------------

  const applyQueuedFeeUpdate = useCallback((): void => {
    // Guard: only apply if queued data is for the current config
    if (queuedConfigIdRef.current !== activeConfigIdRef.current) {
      // Stale queue — clear without applying
      queuedConfigIdRef.current = null;
      queuedCommissionRef.current = null;
      setHasQueuedFeeUpdate(false);
      setDismissedFeeConflict(false);
      return;
    }

    // Apply queued state + update baseline
    setLocalCommission(queuedCommissionRef.current);
    baselineCommissionRef.current = JSON.stringify(queuedCommissionRef.current);

    // Clear queue
    queuedConfigIdRef.current = null;
    queuedCommissionRef.current = null;
    setHasQueuedFeeUpdate(false);
    setDismissedFeeConflict(false);
  }, []);

  // ---------------------------------------------------------------------------
  // Dirty Computation (render body — per CODING_STANDARDS)
  // ---------------------------------------------------------------------------

  const commissionJson = JSON.stringify(localCommission);
  const isCommissionDirty = commissionJson !== baselineCommissionRef.current;

  // Sync JSON snapshot ref (read by sync guard)
  localCommissionJsonRef.current = commissionJson;

  // Sync dirty/discard/save refs for parent (MobileFeeEditorPage) to read synchronously
  isDirtyRef.current = isCommissionDirty;

  discardRef.current = activeFeeConfig
    ? () => {
        setLocalCommission(activeFeeConfig.commission);
        if (hasQueuedFeeUpdate) {
          applyQueuedFeeUpdate();
        }
      }
    : null;

  saveRef.current = activeFeeConfig ? handleSave : null;

  // ---------------------------------------------------------------------------
  // Reactive State Bridge (page-level persistence UI)
  // ---------------------------------------------------------------------------

  const hasActiveConfig = !!activeFeeConfig;

  useEffect(() => {
    onEditorStateChange?.({ isDirty: isCommissionDirty, isSaving, hasActiveConfig });
  }, [isCommissionDirty, isSaving, hasActiveConfig, onEditorStateChange]);

  // ---------------------------------------------------------------------------
  // Dirty→Clean Effect: Apply Queued Update
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isCommissionDirty && hasQueuedFeeUpdate) {
      applyQueuedFeeUpdate();
    }
  }, [isCommissionDirty, hasQueuedFeeUpdate, applyQueuedFeeUpdate]);

  // ---------------------------------------------------------------------------
  // Ref Cleanup on Unmount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      isDirtyRef.current = false;
      discardRef.current = null;
      saveRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Cleanup only: refs are stable across renders
  }, []);

  // ---------------------------------------------------------------------------
  // Fee Config Path (for Customize Fees action)
  // ---------------------------------------------------------------------------

  const feeConfigPath = useMemo(() => {
    if (!selectedNode) return null;
    return buildFeeConfigPath(selectedNode);
  }, [selectedNode]);

  // ---------------------------------------------------------------------------
  // Customize Fees Action (inherited → create new config)
  // ---------------------------------------------------------------------------

  const [isCustomizing, setIsCustomizing] = useState(false);

  const handleCustomize = useCallback(async (): Promise<void> => {
    if (!feeConfigPath) return;

    setIsCustomizing(true);
    try {
      const result = await addFeeConfiguration(feeConfigPath);
      if (result.success) {
        onNodeSelect(result.nodeId);
        showToast({ title: 'Fee configuration created', color: 'success' });
      } else {
        showToast({ title: 'Failed to create fee configuration', color: 'danger' });
      }
    } finally {
      setIsCustomizing(false);
    }
  }, [feeConfigPath, addFeeConfiguration, onNodeSelect, showToast]);

  // ---------------------------------------------------------------------------
  // Inherit from Parent Action (edit → remove custom config)
  // ---------------------------------------------------------------------------

  const [isInheritConfirmOpen, setIsInheritConfirmOpen] = useState(false);
  const [isInheriting, setIsInheriting] = useState(false);

  const handleInheritConfirm = useCallback(async (): Promise<void> => {
    if (!activeFeeConfig || !selectedNode) return;

    setIsInheriting(true);
    localRemovalRef.current = true;
    try {
      const success = await removeFeeConfiguration(activeFeeConfig.id);
      if (success) {
        showToast({ title: 'Now inheriting fees from parent', color: 'success' });
        setIsInheritConfirmOpen(false);
        // Leaf nodes disappear from the tree after archiving — navigate to ancestor.
        // Mid-tier nodes stay (descendants keep them alive) and transition to inherited mode in-place.
        if (selectedNode.children.length === 0 && nearestAncestorNodeId) {
          // Reset dirty ref before navigation — config was removed so dirty state is stale.
          // Prevents navigation guard from blocking this programmatic navigation.
          isDirtyRef.current = false;
          onNodeSelect(nearestAncestorNodeId);
        }
      } else {
        localRemovalRef.current = false;
        showToast({ title: 'Failed to remove custom fees', color: 'danger' });
      }
    } finally {
      setIsInheriting(false);
    }
  }, [activeFeeConfig, removeFeeConfiguration, showToast, selectedNode, nearestAncestorNodeId, isDirtyRef, onNodeSelect]);

  // ---------------------------------------------------------------------------
  // Notes Popover State
  // ---------------------------------------------------------------------------

  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Sync notes text when popover opens or config changes
  useEffect(() => {
    if (isNotesOpen && activeFeeConfig) {
      setNotesText(activeFeeConfig.notes ?? '');
    }
  }, [isNotesOpen, activeFeeConfig]);

  const handleSaveNotes = useCallback(async (): Promise<void> => {
    if (!activeFeeConfig) return;

    // No-op if unchanged
    const currentNotes = activeFeeConfig.notes ?? '';
    if (notesText === currentNotes) {
      setIsNotesOpen(false);
      return;
    }

    setIsSavingNotes(true);
    try {
      const success = await updateFeeConfiguration(activeFeeConfig.id, { notes: notesText });
      if (success) {
        setIsNotesOpen(false);
      } else {
        showToast({ title: 'Failed to save notes', color: 'danger' });
      }
    } finally {
      setIsSavingNotes(false);
    }
  }, [activeFeeConfig, notesText, updateFeeConfiguration, showToast]);

  // ---------------------------------------------------------------------------
  // Verification (rendered in footer, not inside CommissionTabContent)
  // ---------------------------------------------------------------------------

  const handleVerify = useCallback((): void => {
    if (!localCommission) return;
    setLocalCommission({
      ...localCommission,
      verificationStatus: 'userVerified',
      lastVerifiedAt: new Date().toISOString(),
    });
  }, [localCommission]);

  const canVerify = localCommission && (
    needsVerification(localCommission.verificationStatus) ||
    localCommission.verificationStatus === 'verified'
  );

  // ---------------------------------------------------------------------------
  // Taxonomy Path (non-clickable context line)
  // ---------------------------------------------------------------------------

  const taxonomyPath = useMemo((): string => {
    if (!selectedNode) return '';

    const { categoryPath, type, label } = selectedNode;

    // Map category segments to display labels
    const segments: string[] = categoryPath.map((segment, i) => {
      if (i === 0) {
        return ASSET_CLASS_TYPE_DISPLAY_MAP[segment as keyof typeof ASSET_CLASS_TYPE_DISPLAY_MAP] ?? segment;
      } else if (i === 1) {
        return MARKET_SEGMENT_DISPLAY_MAP[segment as keyof typeof MARKET_SEGMENT_DISPLAY_MAP] ?? segment;
      } else {
        return SUB_ASSET_CLASS_DISPLAY_MAP[segment as keyof typeof SUB_ASSET_CLASS_DISPLAY_MAP] ?? segment;
      }
    });

    // For instrument nodes, append the instrument label
    if (type === 'instrument') {
      segments.push(label);
    }

    // Fallback: if no segments, use the node label directly
    return segments.length > 0 ? segments.join(' › ') : label;
  }, [selectedNode]);

  // ---------------------------------------------------------------------------
  // Render Guards
  // ---------------------------------------------------------------------------

  if (!selectedNode) {
    return (
      <div className="flex items-center justify-center py-4 text-text-tertiary text-base">
        Select a fee node to configure
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Computed UI State
  // ---------------------------------------------------------------------------

  const showFeeConflictBanner = hasQueuedFeeUpdate && isCommissionDirty && !dismissedFeeConflict;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <Card radius="none" shadow="none" className="flex flex-col min-h-0 h-full">
        {/* Header: Title + Taxonomy + Back */}
        <CardHeader className="shrink-0 flex-col items-start px-2 py-2">
          <div className="flex items-center justify-between gap-2 w-full">
            <div className="min-w-0 flex-1">
              <span className="text-base font-medium text-text-primary truncate block">
                {selectedNode.label} Commissions
              </span>
              <p className="text-sm text-text-tertiary truncate" title={taxonomyPath}>{taxonomyPath}</p>
            </div>
            <Button
              size="sm"
              variant="light"
              startContent={<ArrowLeft size={16} />}
              onPress={() => onNodeSelect(null)}
              aria-label="Back to fee configurations"
              className="shrink-0"
            >
              Back
            </Button>
          </div>
          {/* Mode Indicator — inherited mode only */}
          {mode === 'inherited' && (
            <div className="mt-2 w-full">
              {nearestAncestorLabel ? (
                <span className="text-base text-text-secondary">
                  Inherited from{' '}
                  <span className="font-semibold">{nearestAncestorLabel}</span>
                </span>
              ) : (
                <span className="text-base text-text-tertiary">
                  No fee configuration at this level or any ancestor
                </span>
              )}
            </div>
          )}
        </CardHeader>

        {/* Content Area */}
        <CardBody className="flex-1 min-h-0 p-0 flex flex-col">
          {/* Cross-tab conflict banner */}
          {showFeeConflictBanner && (
            <div className="shrink-0 mx-2 my-2 flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 px-3 py-2">
              <AlertTriangle size={16} className="text-warning-600 shrink-0 mt-0.5" />
              <p className="text-sm text-warning-700 flex-1">
                External changes received. Save to keep your edits, or discard to load the latest version.
              </p>
              <Button
                variant="light"
                size="sm"
                className="shrink-0"
                onPress={() => setDismissedFeeConflict(true)}
              >
                Dismiss
              </Button>
            </div>
          )}
          <ScrollShadow hideScrollBar className="flex-1 min-h-0">
            {mode === 'inherited' ? (
              nearestAncestorConfig ? (
                /* Inherited mode with ancestor config — read-only display */
                <div className="flex flex-col">
                  <CommissionTabContent
                    commission={nearestAncestorConfig.commission}
                    mode="inherited"
                    accountCurrency={accountCurrency}
                    hideVerification
                  />
                  <div className="flex flex-col items-center gap-2 px-3 py-3 border-t border-default-100">
                    {feeConfigPath ? (
                      <Button
                        color="primary"
                        variant="flat"
                        size="sm"
                        onPress={handleCustomize}
                        isLoading={isCustomizing}
                      >
                        Customize Fees
                      </Button>
                    ) : (
                      <p className="text-sm text-text-tertiary">
                        Fee configurations can be added at market segment level or below
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                /* Inherited mode without config — empty state */
                <div className="flex-1 flex flex-col items-center justify-center px-3 py-8 gap-3">
                  <p className="text-base text-text-tertiary text-center">
                    No fee configuration exists at this level or any ancestor
                  </p>
                  {feeConfigPath ? (
                    <Button
                      color="primary"
                      variant="flat"
                      size="sm"
                      onPress={handleCustomize}
                      isLoading={isCustomizing}
                    >
                      Customize Fees
                    </Button>
                  ) : (
                    <p className="text-sm text-text-tertiary">
                      Fee configurations can be added at market segment level or below
                    </p>
                  )}
                </div>
              )
            ) : (
              /* Edit mode — editable commission form */
              localCommission ? (
                <CommissionTabContent
                  commission={localCommission}
                  mode="edit"
                  accountCurrency={accountCurrency}
                  onChange={handleCommissionChange}
                  hideVerification
                />
              ) : null
            )}
          </ScrollShadow>
        </CardBody>

        {/* Footer — domain actions (edit mode only); Save/Discard owned by MobileFeeEditorPage */}
        {activeFeeConfig && localCommission && (
          <CardFooter className="shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-t border-default-200">
            <div className="flex items-center gap-2 min-w-0">
              <Chip
                size="sm"
                variant="flat"
                color={VERIFICATION_STATUS_COLOR_MAP[localCommission.verificationStatus]}
              >
                {VERIFICATION_STATUS_DISPLAY_MAP[localCommission.verificationStatus]}
              </Chip>
              {canVerify && (
                <Button
                  size="sm"
                  variant="flat"
                  color="success"
                  startContent={<ShieldCheck size={14} />}
                  onPress={handleVerify}
                >
                  Verify
                </Button>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {mode === 'edit' && nearestAncestorConfig && (
                <Button
                  size="sm"
                  variant="flat"
                  color="warning"
                  onPress={() => setIsInheritConfirmOpen(true)}
                >
                  Inherit from Parent
                </Button>
              )}
              {/* Notes Popover */}
              <Popover
                isOpen={isNotesOpen}
                onOpenChange={setIsNotesOpen}
                placement="top-end"
              >
                <PopoverTrigger>
                  <Button
                    size="sm"
                    variant="light"
                    color={activeFeeConfig.notes ? 'primary' : 'default'}
                    aria-label="Fee configuration notes"
                    startContent={<NotepadText size={16} />}
                  >
                    Notes
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-3">
                  <div className="w-72 flex flex-col gap-2">
                    <p className="text-xs font-medium text-text-secondary">
                      Notes
                    </p>
                    <TextArea
                      value={notesText}
                      onValueChange={setNotesText}
                      placeholder="Add notes about this fee configuration..."
                      minRows={3}
                      maxRows={8}
                      size="sm"
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        color="primary"
                        variant="flat"
                        onPress={handleSaveNotes}
                        isLoading={isSavingNotes}
                        isDisabled={notesText === (activeFeeConfig.notes ?? '')}
                        startContent={!isSavingNotes ? <Save size={14} /> : undefined}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </CardFooter>
        )}
      </Card>

      {/* Inherit from Parent Confirmation Modal */}
      <Modal
        isOpen={isInheritConfirmOpen}
        onOpenChange={(open) => !open && setIsInheritConfirmOpen(false)}
        isDismissable={!isInheriting}
        hideCloseButton={isInheriting}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader>Inherit from Parent</ModalHeader>
          <ModalBody>
            <p className="text-text-secondary">
              Remove custom fees for &quot;{selectedNode.label}&quot; and inherit from
              &quot;{nearestAncestorLabel}&quot;?
            </p>
            <p className="text-sm text-text-tertiary">
              The custom configuration will be archived and can be restored later.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsInheritConfirmOpen(false)} isDisabled={isInheriting}>
              Cancel
            </Button>
            <Button color="warning" onPress={handleInheritConfirm} isLoading={isInheriting}>
              Inherit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MobileFeeEditor;
